/**
 * /marketplace/:id — Listing detail page + in-app messaging.
 *
 * Public read; contact + report + delete require sign-in.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Loader2, MapPin, ShieldCheck, Flag, MessageSquare, Trash2, Eye, ArrowLeft,
  ChevronLeft, ChevronRight, DollarSign, Send, X, Tag,
} from "lucide-react";

const REPORT_REASONS = [
  { key: "spam",            label: "Spam or duplicate" },
  { key: "scam",            label: "Suspected scam / fraud" },
  { key: "prohibited",      label: "Prohibited item" },
  { key: "wrong_category",  label: "Wrong category" },
  { key: "explicit_photo",  label: "Explicit / inappropriate photo" },
  { key: "sold_elsewhere",  label: "Already sold elsewhere" },
  { key: "other",           label: "Other" },
];

export default function MarketplaceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/marketplace/${id}`);
        if (!cancel) setListing(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Listing not found.");
        nav("/marketplace", { replace: true });
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [id, nav]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;
  if (!listing) return null;

  const isOwner = user && user.id === listing.user_id;
  const priceText = listing.is_free
    ? "FREE"
    : listing.price != null
      ? `$${Number(listing.price).toLocaleString()}${listing.accepts_offers ? " · OBO" : ""}`
      : (listing.accepts_offers ? "Make an offer" : "—");

  const remove = async () => {
    if (!confirm("Delete this listing?")) return;
    try {
      await http.delete(`/marketplace/${id}`);
      toast.success("Listing deleted.");
      nav("/marketplace");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="marketplace-detail">
      <Link to="/marketplace" className="wofy-btn-ghost text-xs mb-4 inline-flex">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to marketplace
      </Link>

      <div className="grid lg:grid-cols-[minmax(0,3fr)_2fr] gap-8">
        <PhotoGallery photos={listing.photos} idx={photoIdx} setIdx={setPhotoIdx} />

        <div>
          <div className="flex items-center gap-2 text-xs">
            <span
              className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full"
              style={{
                background: listing.listing_type === "looking_for" ? "#7c3aed" : "#2563eb",
                color: "#fff",
              }}
            >
              {listing.listing_type === "looking_for" ? "Looking for" : "For sale"}
            </span>
            <span className="text-[10px] uppercase font-mono opacity-70">{listing.category?.replace(/_/g, " ")}</span>
            {listing.condition && listing.condition !== "n_a" && (
              <span className="text-[10px] uppercase font-mono opacity-70">· {listing.condition.replace(/_/g, " ")}</span>
            )}
          </div>
          <h1 className="font-display text-3xl mt-2">{listing.title}</h1>
          <div className="mt-2 text-2xl font-display" style={{ color: listing.is_free ? "#059669" : "var(--wofy-text)" }}>
            {priceText}
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            <MapPin className="w-3.5 h-3.5" />
            <span>{[listing.city, listing.state, listing.country].filter(Boolean).join(", ")}</span>
            <span>·</span>
            <span>{new Date(listing.bumped_at || listing.created_at).toLocaleDateString()}</span>
            <span>·</span>
            <Eye className="w-3.5 h-3.5" />
            <span>{listing.view_count || 0} views</span>
          </div>

          {listing.description && (
            <p className="mt-4 text-sm whitespace-pre-wrap" style={{ color: "var(--wofy-text)" }}>
              {listing.description}
            </p>
          )}

          {/* Seller card */}
          <div className="mt-6 rounded-xl border p-4" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="flex items-center gap-3">
              {listing.author_avatar ? (
                <img src={listing.author_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-black/10" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{listing.author_name}</div>
                <div className="text-[10px] uppercase font-mono flex items-center gap-2 mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
                  {listing.author_verified_phone && (
                    <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" /> Phone verified</span>
                  )}
                  <span>· {listing.author_tenure_days || 0}d on Social Pulse</span>
                </div>
                {listing.author_bio && <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{listing.author_bio}</p>}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            {!isOwner && (
              user ? (
                <button
                  data-testid="contact-seller-btn"
                  onClick={() => setContactOpen(true)}
                  className="wofy-btn-primary text-sm"
                >
                  <MessageSquare className="w-4 h-4" /> Contact seller
                </button>
              ) : (
                <Link to="/login" className="wofy-btn-primary text-sm">
                  <MessageSquare className="w-4 h-4" /> Sign in to contact
                </Link>
              )
            )}
            {user && !isOwner && (
              <button data-testid="report-btn" onClick={() => setReportOpen(true)} className="wofy-btn-ghost text-sm">
                <Flag className="w-4 h-4" /> Report
              </button>
            )}
            {isOwner && (
              <button data-testid="delete-btn" onClick={remove} className="wofy-btn-ghost text-sm text-red-600">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>

          {isOwner && <SellerThread listingId={id} />}
        </div>
      </div>

      {contactOpen && <ContactDialog listingId={id} onClose={() => setContactOpen(false)} />}
      {reportOpen && <ReportDialog listingId={id} onClose={() => setReportOpen(false)} />}
    </div>
  );
}

// -------- Photo gallery --------
function PhotoGallery({ photos = [], idx, setIdx }) {
  if (!photos.length) {
    return (
      <div className="aspect-square rounded-2xl bg-black/5 flex items-center justify-center opacity-40 text-sm">
        No photos
      </div>
    );
  }
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);
  return (
    <div>
      <div className="relative aspect-square rounded-2xl overflow-hidden bg-black/5">
        <img src={photos[idx]} alt="" className="w-full h-full object-contain" />
        {photos.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1.5 shadow"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1.5 shadow"><ChevronRight className="w-4 h-4" /></button>
            <div className="absolute bottom-2 right-2 text-xs font-mono bg-black/60 text-white px-2 py-0.5 rounded-full">{idx + 1}/{photos.length}</div>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div className="mt-2 grid grid-cols-6 gap-1">
          {photos.map((p, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`aspect-square rounded overflow-hidden border ${i === idx ? "border-black" : "border-transparent"}`}>
              <img src={p} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -------- Contact dialog --------
function ContactDialog({ listingId, onClose }) {
  const [body, setBody] = useState("Hi — is this still available?");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post(`/marketplace/${listingId}/messages`, { body });
      toast.success("Message sent!");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <form onSubmit={submit} className="max-w-md w-full rounded-2xl p-6" style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl">Message seller</h3>
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-xs"><X className="w-4 h-4" /></button>
        </div>
        <textarea
          data-testid="contact-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          maxLength={2000}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <button data-testid="contact-send-btn" disabled={busy || body.length < 2} className="mt-3 w-full wofy-btn-primary justify-center text-sm disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}

// -------- Report dialog --------
function ReportDialog({ listingId, onClose }) {
  const [reason, setReason] = useState("spam");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post(`/marketplace/${listingId}/report`, { reason, detail: detail || null });
      toast.success("Reported — thanks for keeping the marketplace clean.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit report.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <form onSubmit={submit} className="max-w-md w-full rounded-2xl p-6" style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl">Report listing</h3>
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-xs"><X className="w-4 h-4" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Reason</span>
          <select data-testid="report-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }}>
            {REPORT_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="block mb-3">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Details (optional)</span>
          <textarea data-testid="report-detail" value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} maxLength={500} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <button data-testid="report-send-btn" disabled={busy} className="w-full wofy-btn-primary justify-center text-sm disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
          Submit report
        </button>
      </form>
    </div>
  );
}

// -------- Seller thread (visible to owner only) --------
function SellerThread({ listingId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get(`/marketplace/${listingId}/messages`).then((r) => setMessages(r.data.messages || [])).catch(() => {}).finally(() => setLoading(false));
  }, [listingId]);
  if (loading) return null;
  return (
    <div className="mt-6 rounded-xl border p-4" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: "var(--wofy-text-soft)" }}>Inbox for this listing</div>
      {messages.length === 0 && <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No messages yet. Buyers will show up here.</p>}
      <ul className="space-y-3">
        {messages.map((m) => (
          <li key={m.id} className="text-sm">
            <div className="flex items-center gap-2 text-[10px] uppercase font-mono" style={{ color: "var(--wofy-text-soft)" }}>
              <span>{m.from_user_name}</span>
              <span>· {new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-1">{m.body}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
